import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card.tsx';
import type { ProductCatalogItem } from '@ozon-ai/ozon/contracts';
import { cn } from 'cn';
import { type ReactNode } from 'react';

export function CatalogProductPreviewPopup({
  item,
  title,
}: {
  item?: ProductCatalogItem | null;
  title: string;
}) {
  const name = item?.name || title;
  const imageUrl = item?.imageUrl;

  return (
    <>
      {imageUrl ? (
        <img
          src={imageUrl}
          width={200}
          height={200}
          className="mb-2 aspect-square w-full rounded-lg object-cover"
          alt=""
        />
      ) : (
        <div className="mb-2 flex aspect-square w-full items-center justify-center rounded-lg bg-muted text-2xl">
          📦
        </div>
      )}
      <p className="text-sm leading-snug font-medium">{name}</p>
    </>
  );
}

export function CatalogItemThumbnail({
  item,
  title,
  subtitle,
}: {
  item?: ProductCatalogItem | null;
  title: string;
  subtitle?: ReactNode;
}) {
  const imageUrl = item?.imageUrl;

  return (
    <>
      {imageUrl ? (
        <img
          src={imageUrl}
          width={32}
          height={32}
          className="size-8 shrink-0 rounded-md object-cover"
          alt=""
        />
      ) : (
        <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-sm">
          📦
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-semibold">{title}</p>
        {subtitle ? (
          <p className="truncate text-[11px] text-muted-foreground">{subtitle}</p>
        ) : null}
      </div>
    </>
  );
}

type CatalogItemHoverPreviewProps = {
  item?: ProductCatalogItem | null;
  title: string;
  children: ReactNode;
  triggerRender?: React.ComponentProps<typeof HoverCardTrigger>['render'];
  contentClassName?: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
};

export function CatalogItemHoverPreview({
  item,
  title,
  children,
  triggerRender = <span className="flex h-full min-w-0 w-full" />,
  contentClassName,
  side = 'right',
  align = 'start',
}: CatalogItemHoverPreviewProps) {
  const previewTitle = title.trim();
  if (!previewTitle || previewTitle === '—') {
    return children;
  }

  return (
    <HoverCard>
      <HoverCardTrigger render={triggerRender}>{children}</HoverCardTrigger>
      <HoverCardContent
        side={side}
        align={align}
        className={cn('z-[10001] w-56 p-3', contentClassName)}
      >
        <CatalogProductPreviewPopup item={item} title={previewTitle} />
      </HoverCardContent>
    </HoverCard>
  );
}
